import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "../../AxiosInterceptor/axiosInterceptor";

// Helper function to create initial tab state
const createInitialTabState = () => ({
    data: {
        MemberID: '',
        Carrier: '',
        Account: '',
        Group: '',
        PANumber: '',
        GPI: '',
        totalCount: 0,
        page: 0,
        pageSize: 50,
        sortOrder: "asc",
        columnName: "column1"
    },
    search: true,
    error: false,
    isSearchClicked: false,
    loading: false,
    // Carrier dropdown specific state
    carrierList: [],
    carrierLoading: false,
    carrierError: null,
    inputError: {
        MemberID: false,
        Account: false,
        Group: false,
        Carrier: false,
        PANumber: false,
        GPI: false,
    }
});

// Initial state with 9 tabs
const initialState = {
    currentTab: {
        tabIndex: 0,
        tabName: 'Financial Reports',
        mainTableColumns: 8,
        secondTableColumns: 7
    },
    tabs: Array.from({ length: 10 }, () => createInitialTabState())
};

// Async thunk for fetching real carrier data from MNR
export const getCarrierDataFromMNR = createAsyncThunk('reportsTab/getCarrierDataFromMNR',
    async ({ tabIndex }, { rejectWithValue }) => {
        try {
            // Query to get carrier data from new MMT endpoint
            const queryCarriers = `query GetCarrierData {
                GetCarrierData{
                    data {
                        CarrierDescription,
                        Carrier,
                        CocaGroup,
                        CocaGroupDescription
                    }  
                    message
                    success
                }
            }`;

            // Add timeout to API call (10 seconds)
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('API timeout')), 10000);
            });

            // Use the new MMT carrier endpoint
            const apiPromise = axiosInstance.post('https://authextn-dev-api.optum.com/getcarrier',
                JSON.stringify({ query: queryCarriers, variables: {} })
            );

            const response = await Promise.race([apiPromise, timeoutPromise]);

            if (response.data.errors && response.data.errors.length > 0) {
                const errorMessages = response.data.errors.map(err => err.message).join(', ');
                return rejectWithValue(`GraphQL errors: ${errorMessages}`);
            }

            // Check if the query was successful
            const carrierQueryResult = response.data.data.GetCarrierData;
            if (!carrierQueryResult.success) {
                return rejectWithValue(`GetCarrierData API failed: ${carrierQueryResult.message}`);
            }

            // Extract carrier data from the response
            const carrierData = carrierQueryResult.data || [];

            // If no data returned, return error
            if (carrierData.length === 0) {
                return rejectWithValue('No carrier data returned from API');
            }

            // Create unique carriers based on Carrier field
            const uniqueCarriers = carrierData.reduce((acc, item) => {
                // Skip items without a valid Carrier field
                if (!item.Carrier || item.Carrier.trim() === '') {
                    return acc;
                }

                const existing = acc.find(c => c.carrier === item.Carrier);
                if (!existing) {
                    acc.push({
                        carrier: item.Carrier,
                        carrierDescription: item.CarrierDescription || item.Carrier,
                        cocaGroup: item.CocaGroup || '',
                        cocaGroupDescription: item.CocaGroupDescription || ''
                    });
                }
                return acc;
            }, []);

            // If no valid carriers after processing, return error
            if (uniqueCarriers.length === 0) {
                return rejectWithValue('No valid carriers found in API response');
            }

            // Convert to the format expected by the table
            const formattedData = uniqueCarriers.slice(0, 50).map((carrierInfo, index) => ({
                id: `tab${tabIndex}_carrier_${index + 1}`,
                carrier: carrierInfo.carrier,
                carrierDescription: carrierInfo.carrierDescription,
                cocaGroup: carrierInfo.cocaGroup,
                cocaGroupDescription: carrierInfo.cocaGroupDescription
            }));

            return {
                tabIndex,
                data: formattedData,
                totalCount: formattedData.length
            };
        } catch (err) {
            return rejectWithValue(`API failed: ${err.message}`);
        }
    }
);

const reportsTabSlice = createSlice({
    name: 'reportsTab',
    initialState,
    reducers: {
        currentData: (state, action) => {
            const { tabIndex, data } = action.payload;
            if (state.tabs[tabIndex]) {
                state.tabs[tabIndex].data = { ...state.tabs[tabIndex].data, ...data };
            }
        },
        clearData: (state, action) => {
            const { tabIndex } = action.payload;
            if (state.tabs[tabIndex]) {
                state.tabs[tabIndex].data = createInitialTabState().data;
                state.tabs[tabIndex].search = true;
                state.tabs[tabIndex].inputError = createInitialTabState().inputError;
                state.tabs[tabIndex].error = false;
            }
        },
        resetData: (state, action) => {
            const { tabIndex } = action.payload;
            if (state.tabs[tabIndex]) {
                state.tabs[tabIndex].data = createInitialTabState().data;
                state.tabs[tabIndex].search = false;
                state.tabs[tabIndex].inputError = createInitialTabState().inputError;
                state.tabs[tabIndex].error = false;
            }
        },
        updateSearch: (state, action) => {
            const { tabIndex, value } = action.payload;
            if (state.tabs[tabIndex]) {
                state.tabs[tabIndex].search = value;
            }
        },
        searchClicked: (state, action) => {
            const { tabIndex, value } = action.payload;
            if (state.tabs[tabIndex]) {
                state.tabs[tabIndex].isSearchClicked = value;
            }
        },
        updateInputError: (state, action) => {
            const { tabIndex, error } = action.payload;
            if (state.tabs[tabIndex]) {
                state.tabs[tabIndex].inputError = { ...state.tabs[tabIndex].inputError, ...error };
            }
        },
        setCurrentTab: (state, action) => {
            state.currentTab = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Handle getCarrierDataFromMNR
            .addCase(getCarrierDataFromMNR.pending, (state, action) => {
                const tabIndex = action.meta.arg.tabIndex;
                if (state.tabs[tabIndex]) {
                    state.tabs[tabIndex].carrierLoading = true;
                    state.tabs[tabIndex].carrierError = null;
                }
            })
            .addCase(getCarrierDataFromMNR.fulfilled, (state, action) => {
                const { tabIndex, data } = action.payload;
                if (state.tabs[tabIndex]) {
                    state.tabs[tabIndex].carrierLoading = false;
                    state.tabs[tabIndex].carrierList = data || [];
                    state.tabs[tabIndex].carrierError = null;
                }
            })
            .addCase(getCarrierDataFromMNR.rejected, (state, action) => {
                const { tabIndex } = action.payload || {};
                if (tabIndex !== undefined && state.tabs[tabIndex]) {
                    state.tabs[tabIndex].carrierLoading = false;
                    state.tabs[tabIndex].carrierError = (action.payload && action.payload.error) || 'Failed to load carriers';
                }
            });
    }
});

export const {
    currentData,
    clearData,
    resetData,
    updateSearch,
    searchClicked,
    updateInputError,
    setCurrentTab
} = reportsTabSlice.actions;

export default reportsTabSlice.reducer;
